type AuthResult = { message?: string; code?: string; twoFactorRedirect?: boolean };
const message = document.getElementById('auth-message') as HTMLDivElement;
function notice(text: string, error = false): void {
  message.textContent = text;
  message.className = error ? 'notice err' : 'notice';
  message.hidden = false;
  message.scrollIntoView({ block: 'nearest' });
}
async function api<T = AuthResult>(path: string, body?: Record<string, unknown>): Promise<T> {
  const response = await fetch(`/admin/auth${path}`, {
    method: body ? 'POST' : 'GET', credentials: 'same-origin',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json() as AuthResult;
  if (!response.ok) {
    if (response.status === 429) throw new Error('Too many attempts. Please wait before trying again.');
    if (path === '/sign-in/email') throw new Error('Incorrect email or password.');
    if (response.status >= 500) throw new Error('Authentication is temporarily unavailable. Please try again.');
    throw new Error(data.message ?? 'Could not complete this request. Please sign in again.');
  }
  return data as T;
}
function nextPage(form: HTMLFormElement): string {
  const next = String(new FormData(form).get('next') ?? '');
  return /^\/admin(?:\/|$)/.test(next) && !/^\/admin\/(?:auth|login|reset-password|forgot-password)(?:\/|\?|$)/.test(next) ? next : '/admin';
}
if (document.querySelector('[data-auth-form="reset"]')) {
  // Keep the token in the form only so it is not retained in browser history or copied URLs.
  history.replaceState(null, '', location.pathname);
}
if (new URLSearchParams(location.search).get('reset') === '1') notice('Password reset. Sign in with your new password.');

for (const form of document.querySelectorAll<HTMLFormElement>('[data-auth-form]')) {
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    if (submit.disabled) return;
    const values = new FormData(form);
    const value = (name: string) => String(values.get(name) ?? '');
    const mode = form.dataset.authForm;
    if (['reset', 'change'].includes(mode ?? '') && value('newPassword') !== value('confirmPassword')) {
      notice('The new passwords do not match.', true);
      return;
    }
    submit.disabled = true;
    message.hidden = true;
    try {
      switch (mode) {
        case 'login': {
          const data = await api('/sign-in/email', { email: value('email').trim().toLowerCase(), password: value('password'), rememberMe: false });
          if (data.twoFactorRedirect) throw new Error('Could not complete sign-in. Please contact an administrator.');
          location.assign(nextPage(form));
          break;
        }
        case 'forgot':
          await api('/request-password-reset', { email: value('email').trim().toLowerCase(), redirectTo: `${location.origin}/admin/reset-password` });
          notice('If an administrator account exists for that email, a reset link has been sent.');
          form.reset();
          break;
        case 'reset':
          await api('/reset-password', { token: value('token'), newPassword: value('newPassword') });
          location.assign('/admin/login?reset=1');
          break;
        case 'change':
          await api('/change-password', { currentPassword: value('currentPassword'), newPassword: value('newPassword'), revokeOtherSessions: true });
          form.reset();
          notice('Password changed. Your other devices have been signed out.');
          await loadSessions();
          break;
        case 'revoke-all':
          await api('/revoke-sessions', {});
          location.assign('/admin/login');
          break;
      }
    } catch (error) { notice(error instanceof Error ? error.message : 'Could not complete this request.', true); }
    finally { submit.disabled = false; }
  });
}

async function loadSessions(): Promise<void> {
  const container = document.getElementById('auth-sessions');
  if (!container) return;
  try {
    const sessions = await api<{ token: string; userAgent?: string; createdAt: string; expiresAt: string }[]>('/list-sessions');
    container.replaceChildren();
    for (const session of sessions) {
      const row = document.createElement('div');
      row.className = 'auth-session';
      const label = document.createElement('p');
      label.textContent = `${session.userAgent || 'Browser session'} · Signed in ${new Date(session.createdAt).toLocaleString()}`;
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'act quiet'; button.textContent = 'Sign out device';
      button.addEventListener('click', async () => {
        button.disabled = true;
        try { await api('/revoke-session', { token: session.token }); location.reload(); }
        catch { notice('Could not sign out this device. Please try again.', true); button.disabled = false; }
      });
      row.append(label, button); container.append(row);
    }
  } catch { container.textContent = 'Could not load sessions. Sign in again if your session has expired.'; }
}
void loadSessions();
